import { useState } from 'react';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Which cards are open survives a reload: the dashboard is reloaded a lot on
// phones in the field and re-collapsing everything each time is a chore.
// Storage can be missing or blocked (private mode), so both sides just fall
// back to the default.
const STORAGE_PREFIX = 'lt-card-open:';

function readOpen(id, fallback) {
  if (!id) return fallback;
  try {
    const saved = localStorage.getItem(STORAGE_PREFIX + id);
    return saved === null ? fallback : saved === '1';
  } catch {
    return fallback;
  }
}

function writeOpen(id, open) {
  if (!id) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + id, open ? '1' : '0');
  } catch {
    // ignore
  }
}

export function useCardOpen(id, defaultOpen = true) {
  const [open, setOpen] = useState(() => readOpen(id, defaultOpen));
  const toggle = () => setOpen((prev) => {
    writeOpen(id, !prev);
    return !prev;
  });
  return [open, toggle];
}

/**
 * A dashboard card with a header row that folds its body away.
 *
 * - `title` / `icon` make up the header; `actions` (a control such as a select)
 *   sits between the title and the chevron and doesn't toggle the card.
 * - `collapsible={false}` with no title renders the children bare, so a card can
 *   be foldable on phones and untouched on desktop.
 * - `fillBody` lets the body stretch to the card's height (desktop cards are
 *   sized by percentages, and the graph fills whatever it is given).
 * - `collapsedSx` is merged into `sx` while closed, to drop a fixed height.
 */
function CollapsibleCard({
  id,
  title,
  icon,
  actions,
  defaultOpen = true,
  collapsible = true,
  fillBody = false,
  sx,
  collapsedSx,
  headerSx,
  titleSx,
  bodySx,
  children,
}) {
  const [open, toggle] = useCardOpen(id, defaultOpen);
  const expanded = !collapsible || open;
  const label = typeof title === 'string' ? title : id;

  if (!collapsible && !title) return children;

  const fill = fillBody && expanded;

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        // Cards sit in flex columns; never let one be squeezed below its content
        // (that is what pushed the stats under the footer on phones).
        flexShrink: 0,
        p: 0.75,
        ...sx,
        ...(expanded ? {} : collapsedSx),
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        onClick={collapsible ? toggle : undefined}
        sx={{
          pl: 0.5,
          minHeight: 32,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
          ...headerSx,
        }}
      >
        {icon}
        {typeof title === 'string' ? (
          <Typography
            variant='h6'
            noWrap
            sx={{ flex: 1, minWidth: 0, fontSize: { xl: 20, md: 16, xs: 14 }, fontWeight: 500, ...titleSx }}
          >
            {title}
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
        )}
        {actions && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {actions}
          </Box>
        )}
        {collapsible && (
          <IconButton
            size='small'
            aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
            aria-expanded={open}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Stack>

      <Collapse
        in={expanded}
        timeout='auto'
        sx={{
          // Once entered, Collapse pins its own height to `auto`. To let the body
          // take the rest of a percentage-sized card the root grows as a flex
          // item and the wrappers pass that height down.
          flex: fill ? 1 : '0 0 auto',
          minHeight: 0,
          '& .MuiCollapse-wrapper': { height: fill ? '100%' : undefined },
          '& .MuiCollapse-wrapperInner': fill
            ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }
            : {},
        }}
      >
        {/* Only a filling body is a flex item: otherwise percentage heights inside
            (the stats strip uses height: 100%) would resolve against it and inflate. */}
        <Box
          sx={{
            pt: 0.5,
            ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
            ...bodySx,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

export default CollapsibleCard;
